import { api, ApiResponse } from './api';

export interface ContratoServicos {
  id: number;
  contrato_id: number;
  tipo: 'internet' | 'telefonia' | 'tv';
  status: 'ativo' | 'bloqueado' | 'cancelado' | 'aguardando_instalacao' | 'aguardando_assinatura';
  data_registro: string;
  plano_id?: number;
  acessos_simultaneos: boolean;
  nas_server?: number | null;
  usuario_pppoe?: string;
  senha_pppoe?: string;
  endereco_ip?: string;
  endereco_mac?: string;
  observacoes?: string;
  data_instalacao?: string;
  data_bloqueio?: string;
  data_cancelamento?: string;
  pool?: string;
  cep_inst?: string;
  cidade_inst?: string;
  estado_inst?: string;
  bairro_inst?: string;
  endereco_inst?: string;
  numero_inst?: string;
  complemento_inst?: string;
  coordenadas_inst?: string;
  referencia_inst?: string;
  codigo_municipio?: string;
  valor?: number;
  desconto?: number;
  acrescimo?: number;
  cclass_fiscal?: string;
  isento?: boolean;
  tipo_meio: 'fibra' | 'radio' | 'cabo_coaxial' | 'par_metalico' | 'satelite';
  tipo_atendimento: 'urbano' | 'rural';
  gera_dici: boolean;
  tipo_moradia?: 'propria' | 'alugado' | null;
  conexao?: 'Online' | 'Offline' | null;
  plano?: {
    id: number;
    nome: string;
    velocidade_download: number;
    velocidade_upload: number;
  };
  contrato?: {
    id: number;
    titulo?: string;
    status: 'ativo' | 'bloqueado' | 'cancelado' | 'aguardando_instalacao' | 'aguardando_assinatura';
    cliente_id?: number;
    cliente?: {
      id: number;
      nome: string;
      cpf_cnpj?: string;
      telefone?: string;
      celular?: string;
      email?: string;
    };
  };
}

export interface GetAllServicosParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  tipo?: string;
  conexao?: string;
  plano_id?: number;
  nas_server?: number;
  tipo_meio?: string;
  startDate?: string;
  endDate?: string;
}

export interface ServicosStats {
  total: number;
  ativos: number;
  bloqueados: number;
  cancelados: number;
  aguardando_instalacao: number;
  aguardando_assinatura: number;
  internet: number;
  telefonia: number;
  tv: number;
  online?: number;
  offline?: number;
}

export interface CreateContratoServicosData {
  tipo: 'internet' | 'telefonia' | 'tv';
  status: 'ativo' | 'bloqueado' | 'cancelado' | 'aguardando_instalacao' | 'aguardando_assinatura';
  plano_id?: number;
  acessos_simultaneos: boolean;
  nas_server?: number | null;
  usuario_pppoe?: string;
  senha_pppoe?: string;
  endereco_ip?: string;
  endereco_mac?: string;
  observacoes?: string;
  pool?: string;
  cep_inst?: string;
  cidade_inst?: string;
  estado_inst?: string;
  bairro_inst?: string;
  endereco_inst?: string;
  numero_inst?: string;
  complemento_inst?: string;
  coordenadas_inst?: string;
  referencia_inst?: string;
  codigo_municipio?: string;
  valor?: number;
  desconto?: number;
  acrescimo?: number;
  cclass_fiscal?: string;
  isento?: boolean;
  tipo_meio?: 'fibra' | 'radio' | 'cabo_coaxial' | 'par_metalico' | 'satelite';
  tipo_atendimento?: 'urbano' | 'rural';
  gera_dici?: boolean;
  tipo_moradia?: 'propria' | 'alugado' | null;
}

export interface UpdateContratoServicosData extends Partial<CreateContratoServicosData> {
  id: number;
}

export interface UpdateStatusData {
  status: 'ativo' | 'bloqueado' | 'cancelado' | 'aguardando_instalacao' | 'aguardando_assinatura';
  observacoes?: string;
}

export class ContratoServicosService {
  /**
   * Lista todos os serviços do sistema com paginação e filtros
   */
  static async getAll(params?: GetAllServicosParams): Promise<ApiResponse<ContratoServicos[]>> {
    try {
      const response = await api.get('/contratos/servicos-adicionais/lista/todas', { params }) as ApiResponse<ContratoServicos[]>;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao carregar serviços');
    }
  }

  /**
   * Retorna as estatísticas gerais dos serviços
   */
  static async getStats(): Promise<ApiResponse<ServicosStats>> {
    try {
      const response = await api.get('/contratos/servicos-adicionais/stats/geral') as ApiResponse<ServicosStats>;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao carregar estatísticas dos serviços');
    }
  }

  /**
   * Lista todos os serviços de um contrato
   */
  static async getByContractId(contractId: number): Promise<ApiResponse<ContratoServicos[]>> {
    try {
      const response = await api.get(`/contratos/${contractId}/servicos`) as ApiResponse<ContratoServicos[]>;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao carregar serviços');
    }
  }

  /**
   * Verifica unicidade de campos de conexão
   */
  static async checkUnique(params: { usuario_pppoe?: string; endereco_ip?: string; endereco_mac?: string; exclude_id?: number }): Promise<ApiResponse<{ unique: boolean; field?: string; message?: string }>> {
    try {
      const response = await api.get('/contratos/servico/check-unique', { params }) as ApiResponse<{ unique: boolean; field?: string; message?: string }>;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao verificar unicidade');
    }
  }

  /**
   * Cria um novo serviço para um contrato
   */
  static async create(contractId: number, data: CreateContratoServicosData): Promise<ApiResponse<ContratoServicos>> {
    try {
      const response = await api.post(`/contratos/${contractId}/servicos/cadastrar`, data) as ApiResponse<ContratoServicos>;
      return response;
    } catch (error: any) {
      // Se há erros detalhados do backend, retornar a resposta completa
      if (error.response?.data?.errors) {
        return {
          success: false,
          message: error.response.data.message || 'Erro ao criar serviço',
          errors: error.response.data.errors,
          data: undefined
        };
      }
      throw new Error(error.response?.data?.message || 'Erro ao criar serviço');
    }
  }

  /**
   * Atualiza um serviço existente
   */
  static async update(id: number, data: Partial<CreateContratoServicosData>): Promise<ApiResponse<ContratoServicos>> {
    try {
      const response = await api.put(`/contratos/servico/${id}`, data) as ApiResponse<ContratoServicos>;
      return response;
    } catch (error: any) {
      // Se há erros detalhados do backend, retornar a resposta completa
      if (error.response?.data?.errors) {
        return {
          success: false,
          message: error.response.data.message || 'Erro ao atualizar serviço',
          errors: error.response.data.errors,
          data: undefined
        };
      }
      throw new Error(error.response?.data?.message || 'Erro ao atualizar serviço');
    }
  }

  /**
   * Exclui um serviço
   */
  static async delete(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(`/contratos/servico/${id}`) as ApiResponse<void>;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao excluir serviço');
    }
  }

  /**
   * Altera o status de um serviço
   */
  static async updateStatus(id: number, status: UpdateStatusData): Promise<ApiResponse<ContratoServicos>> {
    try {
      const response = await api.put(`/contratos/servico/${id}/status`, status) as ApiResponse<ContratoServicos>;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao alterar status');
    }
  }

  /**
   * Busca um serviço por ID
   */
  static async getById(id: number): Promise<ApiResponse<ContratoServicos>> {
    try {
      const response = await api.get(`/contratos/servicos-adicionais/${id}`) as ApiResponse<ContratoServicos>;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar serviço');
    }
  }

  /**
   * Transfere um serviço para outro contrato
   */
  static async transferir(servicoId: number, newContractId: number): Promise<ApiResponse<ContratoServicos>> {
    try {
      const response = await api.put(`/contratos/servico/${servicoId}/transferir`, {
        novo_contrato_id: newContractId
      }) as ApiResponse<ContratoServicos>;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao transferir serviço');
    }
  }

  /**
   * Repara a conexão de um serviço (recria no RADIUS e MikroTik)
   */
  static async repararConexao(servicoId: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/contratos/servico/${servicoId}/reparar-conexao`) as ApiResponse<any>;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao reparar conexão');
    }
  }

  /**
   * Libera o MAC address de um serviço e derruba a conexão
   */
  static async liberarMac(servicoId: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/contratos/servico/${servicoId}/liberar-mac`) as ApiResponse<any>;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao liberar MAC');
    }
  }
}

export default ContratoServicosService;
